import type { StockAnalogMatch } from '@/mocks/stockAnalogMock';
import type { ReliableAnalogPick } from '@/services/api';

export type SelfAnalogStrength = {
  hit5: number | null;
  med5: number | null;
  samples: number;
  score: number;
};

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/** Self-analog confirmation from mechanical matches (20-day price fingerprint). */
export function computeSelfAnalogStrength(matches: StockAnalogMatch[]): SelfAnalogStrength {
  const t5 = matches
    .map((m) => m.forwardReturns?.t5)
    .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));

  const hit5 = t5.length ? (100 * t5.filter((v) => v > 0).length) / t5.length : null;
  const med5 = median(t5);

  // Hit rate dominates; median 5d breaks ties (strongest take = high hit + positive median).
  const score =
    hit5 != null && med5 != null
      ? hit5 * 2 + med5
      : hit5 != null
        ? hit5 * 2
        : med5 != null
          ? med5
          : -999;

  return {
    hit5: hit5 != null ? Math.round(hit5 * 10) / 10 : null,
    med5: med5 != null ? Math.round(med5 * 100) / 100 : null,
    samples: t5.length,
    score,
  };
}

export function sortReliablePicksByStrength(
  rows: ReliableAnalogPick[],
  strengthBySymbol: Record<string, SelfAnalogStrength | undefined>,
): ReliableAnalogPick[] {
  return [...rows].sort((a, b) => {
    const sa = strengthBySymbol[a.symbol];
    const sb = strengthBySymbol[b.symbol];
    const scoreA = sa?.score ?? -999;
    const scoreB = sb?.score ?? -999;
    if (scoreB !== scoreA) return scoreB - scoreA;
    const hitA = sa?.hit5 ?? -1;
    const hitB = sb?.hit5 ?? -1;
    if (hitB !== hitA) return hitB - hitA;
    const medA = sa?.med5 ?? -999;
    const medB = sb?.med5 ?? -999;
    if (medB !== medA) return medB - medA;
    return (b.hit_rate ?? 0) - (a.hit_rate ?? 0);
  });
}
